import { useEffect, useState } from "react";
import { Menu, X, Phone, Sparkles } from "lucide-react";
import { LOGO_URL, NAV_LINKS, COMPANY } from "../data/content";
import { BarkWordmark } from "../components/BrandMarks";
import { TikTokIcon, InstagramIcon } from "../components/SocialIcons";

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [active, setActive] = useState("home");

    useEffect(() => {
        const onScroll = () => {
            setScrolled(window.scrollY > 24);
            const sections = NAV_LINKS.map((l) => l.href.slice(1));
            const fromTop = window.scrollY + 160;
            for (let i = sections.length - 1; i >= 0; i--) {
                const el = document.getElementById(sections[i]);
                if (el && el.offsetTop <= fromTop) {
                    setActive(sections[i]);
                    break;
                }
            }
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const close = () => setOpen(false);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-haas-cream/95 backdrop-blur-md shadow-[0_2px_0_rgba(10,17,40,0.08)]"
                    : "bg-haas-cream"
            }`}
        >
            {/* TOP BANNER: quote CTA + social/review icons */}
            <div
                className="bg-haas-ink text-haas-cream"
                data-testid="navbar-top-banner"
            >
                <div className="max-w-7xl mx-auto px-3 sm:px-10 py-2 flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-4">
                    {/* CTA tagline */}
                    <a
                        href={`tel:${COMPANY.phoneTel}`}
                        data-testid="navbar-promo-strip"
                        className="group inline-flex items-center gap-2 text-center text-[10.5px] sm:text-xs font-semibold uppercase tracking-[0.13em] hover:text-haas-gold transition-colors"
                    >
                        <Sparkles className="w-3.5 h-3.5 text-haas-gold flex-shrink-0" />
                        <span>
                            <span className="hidden sm:inline">
                                Contact us now
                            </span>
                            <span className="sm:hidden">Call</span>{" "}
                            <span className="text-haas-gold font-bold">
                                {COMPANY.phone}
                            </span>{" "}
                            ·{" "}
                            <span className="underline decoration-haas-gold">
                                free landscaping quote
                            </span>
                        </span>
                    </a>

                    {/* Icon row: Bark / Trustpilot / TikTok / Instagram */}
                    <div className="flex items-center gap-2">
                        <a
                            href={COMPANY.barkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="navbar-bark-link"
                            aria-label="Luxe Artificial Grass on Bark — 5 stars"
                            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/8 hover:bg-white/15 border border-haas-gold/40 transition-colors"
                        >
                            <BarkWordmark className="h-3 w-auto" />
                            <span className="text-haas-sun text-[9px] font-bold">
                                5★
                            </span>
                        </a>
                        <a
                            href={COMPANY.tiktokUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="navbar-tiktok-link"
                            aria-label="Luxe Artificial Grass on TikTok"
                            className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/8 hover:bg-white text-white hover:text-haas-ink border border-haas-gold/40 transition-colors"
                        >
                            <TikTokIcon className="w-3.5 h-3.5" />
                        </a>
                        <a
                            href={COMPANY.instagramUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="navbar-instagram-link"
                            aria-label="Luxe Artificial Grass on Instagram"
                            className="inline-flex items-center justify-center w-7 h-7 rounded-full text-white border border-haas-gold/40 transition-transform hover:scale-105"
                            style={{
                                background:
                                    "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
                            }}
                        >
                            <InstagramIcon className="w-3.5 h-3.5" />
                        </a>
                    </div>
                </div>
            </div>

            {/* Main bar */}
            <div className="max-w-7xl mx-auto px-4 sm:px-10 flex items-center justify-between h-20 sm:h-24 gap-4">
                {/* Logo */}
                <a
                    href="#home"
                    data-testid="navbar-logo-link"
                    className="flex items-center gap-3 flex-shrink-0"
                >
                    <img
                        src={LOGO_URL}
                        alt="Luxe Artificial Grass Ltd"
                        className="h-14 sm:h-16 w-14 sm:w-16 object-contain rounded-2xl border-2 border-haas-ink shadow-[3px_3px_0_#123D28]"
                        data-testid="navbar-logo-img"
                    />
                    <div className="hidden sm:flex flex-col leading-tight">
                        <span className="font-display font-bold text-xl text-haas-ink tracking-tight">
                            Luxe
                        </span>
                        <span className="text-[9px] uppercase tracking-[0.22em] text-haas-goldDeep font-bold">
                            Artificial Grass Ltd
                        </span>
                    </div>
                </a>

                {/* Center nav */}
                <nav className="hidden lg:flex items-center gap-8">
                    {NAV_LINKS.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={l.testid}
                            className={`nav-link text-sm font-semibold transition-colors whitespace-nowrap ${
                                active === l.href.slice(1)
                                    ? "text-haas-goldDeep active"
                                    : "text-haas-ink hover:text-haas-goldDeep"
                            }`}
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                {/* Right cluster */}
                <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                    {/* Phone — md+ */}
                    <a
                        href={`tel:${COMPANY.phoneTel}`}
                        data-testid="navbar-phone-link"
                        className="hidden md:inline-flex items-center gap-2 px-4 py-2.5 border-2 border-haas-ink text-haas-ink bg-white hover:bg-haas-sun rounded-full text-xs font-bold transition-colors shadow-[3px_3px_0_#123D28] whitespace-nowrap"
                    >
                        <Phone className="w-4 h-4" />
                        {COMPANY.phone}
                    </a>

                    {/* Quote CTA — lg+ */}
                    <a
                        href="#summer-offer"
                        data-testid="navbar-quote-cta"
                        className="hidden lg:inline-flex items-center gap-2 px-5 py-2.5 bg-haas-ink text-haas-cream hover:bg-haas-goldDeep hover:text-haas-ink rounded-full text-xs font-bold transition-colors shadow-[3px_3px_0_#D4AF37] whitespace-nowrap"
                    >
                        Free quote
                    </a>

                    {/* Mobile menu */}
                    <button
                        onClick={() => setOpen(true)}
                        className="lg:hidden p-2 text-haas-ink rounded-full border-2 border-haas-ink bg-white shadow-[2px_2px_0_#123D28]"
                        data-testid="navbar-mobile-open"
                        aria-label="Open menu"
                    >
                        <Menu className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Mobile drawer */}
            {open && (
                <div
                    data-testid="navbar-mobile-drawer"
                    className="fixed inset-0 z-50 bg-haas-cream lg:hidden overflow-y-auto"
                >
                    <div className="flex items-center justify-between h-24 px-5 border-b-2 border-haas-ink/10 sticky top-0 bg-haas-cream">
                        <div className="flex items-center gap-3">
                            <img
                                src={LOGO_URL}
                                alt="Luxe Artificial Grass"
                                className="h-14 w-14 object-contain rounded-xl border-2 border-haas-ink shadow-[3px_3px_0_#123D28]"
                            />
                            <div className="flex flex-col leading-tight">
                                <span className="font-display font-bold text-2xl">
                                    Luxe
                                </span>
                                <span className="text-[9px] uppercase tracking-[0.22em] text-haas-goldDeep font-bold">
                                    Artificial Grass Ltd
                                </span>
                            </div>
                        </div>
                        <button
                            onClick={close}
                            className="p-2 text-haas-ink rounded-full border-2 border-haas-ink bg-white shadow-[3px_3px_0_#123D28]"
                            data-testid="navbar-mobile-close"
                            aria-label="Close menu"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                    <nav className="flex flex-col gap-1 px-5 py-8">
                        {NAV_LINKS.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                onClick={close}
                                data-testid={`mobile-${l.testid}`}
                                className="py-4 border-b border-haas-border font-display font-semibold text-3xl text-haas-ink hover:text-haas-goldDeep"
                            >
                                {l.label}
                            </a>
                        ))}
                        <a
                            href="#summer-offer"
                            onClick={close}
                            data-testid="mobile-navbar-quote-cta"
                            className="mt-8 inline-flex items-center justify-center px-6 py-4 bg-haas-ink text-haas-cream rounded-full font-bold shadow-[4px_4px_0_#D4AF37]"
                        >
                            Claim summer offer
                        </a>
                        <a
                            href={`tel:${COMPANY.phoneTel}`}
                            data-testid="mobile-navbar-phone-link"
                            className="mt-3 inline-flex items-center justify-center gap-2 px-6 py-4 border-2 border-haas-ink text-haas-ink bg-white rounded-full font-bold shadow-[4px_4px_0_#123D28]"
                        >
                            <Phone className="w-4 h-4" />
                            {COMPANY.phone}
                        </a>

                        {/* Reviews row in drawer */}
                        <div className="mt-8 pt-6 border-t-2 border-dashed border-haas-ink/15">
                            <div className="text-xs uppercase tracking-[0.25em] text-haas-goldDeep font-bold mb-4">
                                Our 5-star reviews
                            </div>
                            <div className="grid grid-cols-1 gap-3">
                                <a
                                    href={COMPANY.barkUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={close}
                                    data-testid="mobile-navbar-bark-link"
                                    className="inline-flex items-center justify-center gap-2 px-4 py-3.5 rounded-full bg-[#0E3220] border-2 border-haas-ink shadow-[3px_3px_0_#123D28]"
                                >
                                    <BarkWordmark className="h-4 w-auto" />
                                    <span className="text-haas-sun text-[10px] font-bold uppercase tracking-[0.18em]">
                                        5★
                                    </span>
                                </a>
                            </div>
                        </div>

                        {/* Socials row in drawer */}
                        <div className="mt-6 pt-6 border-t-2 border-dashed border-haas-ink/15">
                            <div className="text-xs uppercase tracking-[0.25em] text-haas-goldDeep font-bold mb-4 text-center">
                                Follow us
                            </div>
                            <div className="flex items-center justify-center gap-4">
                                <a
                                    href={COMPANY.tiktokUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={close}
                                    data-testid="mobile-navbar-tiktok-link"
                                    aria-label="Luxe Artificial Grass on TikTok"
                                    className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-haas-ink text-white border-2 border-haas-ink shadow-[4px_4px_0_#123D28]"
                                >
                                    <TikTokIcon className="w-6 h-6" />
                                </a>
                                <a
                                    href={COMPANY.instagramUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={close}
                                    data-testid="mobile-navbar-instagram-link"
                                    aria-label="Luxe Artificial Grass on Instagram"
                                    className="inline-flex items-center justify-center w-14 h-14 rounded-full text-white border-2 border-haas-ink shadow-[4px_4px_0_#123D28]"
                                    style={{
                                        background:
                                            "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
                                    }}
                                >
                                    <InstagramIcon className="w-6 h-6" />
                                </a>
                            </div>
                        </div>
                    </nav>
                </div>
            )}
        </header>
    );
}
